import { createRoot } from "react-dom/client";
import "./index.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import usePollRefreshToken from "@roman/shared/hooks/auth/usePollRefreshToken";
import { authHeader } from "@roman/shared/lib/auth/authHeader";
import { useAuthStore } from "@roman/shared/store/auth.store";
import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import { HelmetProvider } from "react-helmet-async";
import { createWebEnv } from "./config/webEnv";
import { webEnv } from "./config/webEnv";
import Root from "./routes";

// =============================================================================
// Redirect to miraima.app
// To revert: Comment out or delete the following block
// =============================================================================
const REDIRECT_URL = "https://miraima.app";
window.location.href = `${REDIRECT_URL}${window.location.pathname}${window.location.search}`;
// =============================================================================

const GA_MEASUREMENT_ID = "G-KQR7E0ZMCK";

const AppInitializer = ({ children }: { children: React.ReactNode }) => {
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  usePollRefreshToken();

  useEffect(() => {
    if (webEnv.DEV) return;
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gtagOptions: {
        send_page_view: false,
      },
    });
  }, []);

  // Initialize authHeader and load persisted user data on app initialization
  useEffect(() => {
    const initializeAuth = async () => {
      await authHeader.initialize();
      await useAuthStore.getState().loadPersistedUser();
      setIsAuthInitialized(true);
    };
    initializeAuth();
  }, []);

  // Wait for auth initialization to complete before rendering children
  if (!isAuthInitialized) {
    return null;
  }

  return <>{children}</>;
};

const tagManagerArgs = {
  gtmId: GA_MEASUREMENT_ID,
};

TagManager.initialize(tagManagerArgs);

const container = document.querySelector("#root");

if (container) {
  const env = createWebEnv();
  const root = createRoot(container);
  root.render(
    <HelmetProvider>
      <GoogleOAuthProvider clientId={env.GOOGLE_CLIENT_ID}>
        <AppInitializer>
          <Root />
        </AppInitializer>
      </GoogleOAuthProvider>
    </HelmetProvider>,
  );
} else {
  throw new Error("ルートコンテナが見つかりません");
}
